import ActionButton from "@/components/buttons/ActionButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import { InputNumber } from "@/components/InputNumber";
import { List } from "@/components/list/List";
import { datePickerClearableProps, datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError } from "@/utils/formUtils";
import { tInterval } from "@/utils/translationUtils";
import { toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Col, DatePicker, Form, Row, Space } from "antd";
import { Rule } from "antd/lib/form";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import t from "../../../../app/i18n";
import { rowGutter } from "../../../../common/constants";
import { useCurrentAgentAccount } from "../../../auth/hooks";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { ContractType } from "../../enums";
import type {
  Contract,
  ContractGainerKey,
  CreateUpdateContract,
  CreateUpdateContractGainerIdKey,
  CreateUpdateContractGainerRateKey,
  CreateUpdateContractGainerRecord,
} from "../../types";
import { calculateGainerRecordStartDates } from "../../utils";

const colSpan = 4;

const gainerNumbers = [1, 2, 3, 4, 5] as const;

const getGainerRecordValidationError = (record: CreateUpdateContractGainerRecord | undefined, gainersCount: number) => {
  const gainerIds = gainerNumbers.slice(0, gainersCount).map((gainerNumber) => record?.[`gainer${gainerNumber}Id`]);

  if (gainerIds.some((gainerId) => !gainerId)) {
    return t("validation.notNull");
  }

  if (new Set(gainerIds).size !== gainerIds.length) {
    return t("validation.duplicateGainerWithinSameRecord");
  }

  if (gainersCount <= 1) {
    return undefined;
  }

  const gainerRates = gainerNumbers.slice(0, gainersCount).map((gainerNumber) => record?.[`gainer${gainerNumber}Rate`]);

  if (gainerRates.some((rate) => rate === undefined || rate === null)) {
    return t("validation.notNull");
  }

  const definedGainerRates = gainerRates.filter((rate): rate is number => rate !== undefined && rate !== null);

  if (definedGainerRates.some((rate) => rate < 0)) {
    return t("validation.minNumber", { min: 0 });
  }

  if (definedGainerRates.some((rate) => rate > 100)) {
    return t("validation.maxNumber", { max: 100 });
  }

  const rateSum = definedGainerRates.reduce((sum, rate) => sum + rate, 0);

  return rateSum === 100 ? undefined : t("validation.commissionsRatesSumMustBeOneHundred");
};

type Props = {
  initialContract?: Contract;
  disablePrivilegedInput: boolean;
};

export const ContractGainersFormSection = ({ initialContract, disablePrivilegedInput }: Props) => {
  const { t } = useTranslation();

  const userAccount = useCurrentAgentAccount();

  const form = Form.useFormInstance<CreateUpdateContract>();

  const [gainerCounts, setGainerCounts] = useState<number[]>(
    initialContract?.gainerRecords?.map(
      (r) => [r.gainer1, r.gainer2, r.gainer3, r.gainer4, r.gainer5].filter((g) => !!g).length,
    ) || [1],
  );

  const resolveContractStartDate = () => {
    const contractType = form.getFieldValue(["type"]) as ContractType;

    return form.getFieldValue([
      contractType === ContractType.INSURANCE_CONTRACT ? "effectiveBeginningDate" : "signDate",
    ]) as string;
  };

  const gainerRecordsRules = useMemo(
    () => [
      {
        validator: (_: unknown, records?: CreateUpdateContractGainerRecord[]) => {
          if (!records?.length) {
            return Promise.reject(t("validation.notNull"));
          }

          let error: string | undefined;

          records.forEach((record, recordIndex) => {
            if (error) {
              return;
            }

            error = getGainerRecordValidationError(record, gainerCounts[recordIndex] ?? 1);
          });

          return error ? Promise.reject(error) : Promise.resolve();
        },
      },
    ],
    [gainerCounts],
  );

  const renderStartDateItem = (fieldName: number, hidden: boolean) => (
    <Form.Item
      name={[fieldName, "startDate"]}
      label={t("contract.attrs.gainerRecords.startDate")}
      rules={[validations.notNull]}
      hidden={hidden}
      {...datePickerFormItemProps}
    >
      <DatePicker {...datePickerStandardProps} disabled />
    </Form.Item>
  );

  const handleGainerRecordAdd = (addEmptyGainerRecord: () => void) => {
    addEmptyGainerRecord();

    const newGainerCounts = [...gainerCounts];
    newGainerCounts.push(1);
    setGainerCounts(newGainerCounts);
  };

  const handleGainerRecordDelete = (index: number, remove: (ind: number) => void) => {
    const newGainerCounts = [...gainerCounts];
    newGainerCounts.splice(index, 1);
    setGainerCounts(newGainerCounts);

    const oldGainerRecords = form.getFieldValue(["gainerRecords"]) as CreateUpdateContractGainerRecord[];

    const isLastIndex = index + 1 === oldGainerRecords.length;

    remove(index);

    const recordsAfterRemoving = form.getFieldValue(["gainerRecords"]) as CreateUpdateContractGainerRecord[];

    // Removing the last record leaves the one before it open-ended
    if (isLastIndex) {
      recordsAfterRemoving[index - 1] = {
        ...(recordsAfterRemoving[index - 1] as CreateUpdateContractGainerRecord),
        endDate: undefined,
      };
    }

    form.setFieldsValue({
      gainerRecords: calculateGainerRecordStartDates(recordsAfterRemoving, resolveContractStartDate()),
    });
  };

  const handleGainerAdd = (index: number) => {
    const newGainerCounts = [...gainerCounts];

    if (newGainerCounts[index]) {
      newGainerCounts[index]++;
    }

    setGainerCounts(newGainerCounts);
  };

  const handleGainerRemove = (gainerRecordIndex: number) => {
    const newGainerCounts = [...gainerCounts];

    if (newGainerCounts[gainerRecordIndex]) {
      newGainerCounts[gainerRecordIndex]--;
    }

    setGainerCounts(newGainerCounts);

    // @ts-ignore ts-problem
    const records = [...form.getFieldValue(["gainerRecords"])] as CreateUpdateContractGainerRecord[];
    const record = { ...records[gainerRecordIndex] } as CreateUpdateContractGainerRecord;

    if (!record) {
      return;
    }

    if (newGainerCounts[gainerRecordIndex] === 1) {
      record.gainer1Rate = undefined;
    }

    const removedGainerIndex = gainerCounts[gainerRecordIndex] as number;

    record[`gainer${removedGainerIndex}Id` as Exclude<CreateUpdateContractGainerIdKey, "gainer1Id">] = undefined;
    record[`gainer${removedGainerIndex}Rate` as CreateUpdateContractGainerRateKey] = undefined;

    records[gainerRecordIndex] = record;

    form.setFieldsValue({ gainerRecords: records });
  };

  const handleGainerRecordEndDateChange = (index: number) => {
    const records = form.getFieldValue(["gainerRecords"]) as CreateUpdateContractGainerRecord[];

    if (records[index + 1]) {
      form.setFieldsValue({ gainerRecords: calculateGainerRecordStartDates(records, resolveContractStartDate()) });
      form.validateFields([["gainerRecords", index + 1, "endDate"]]).catch(resolveFormValidationError);
    }
  };

  return (
    <Form.List name="gainerRecords" initialValue={[{}]} rules={gainerRecordsRules}>
      {(fields, { add, remove }) => (
        <>
          <List
            items={fields}
            rowKey={(field) => field.key}
            gap={8}
            split
            style={{
              border: "1px solid var(--ant-color-border)",
              borderRadius: "var(--ant-border-radius-lg)",
              padding: "8px 12px",
            }}
            renderItem={(field) => (
              <div style={{ width: "100%" }}>
                <HiddenInput name={[field.name, "id"]} />
                <HiddenInput name={[field.name, "optimisticLockVersion"]} />

                {fields.length === 1 && renderStartDateItem(field.name, true)}

                {fields.length > 1 && (
                  <Row gutter={rowGutter}>
                    <Col span={colSpan}>{renderStartDateItem(field.name, false)}</Col>
                    <Col span={colSpan}>
                      {field.name < fields.length - 1 && (
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, next) =>
                            prev.gainerRecords[field.name]?.startDate !== next.gainerRecords[field.name]?.startDate
                          }
                        >
                          {({ getFieldValue }) => {
                            const rules: Rule[] = [validations.notNull];
                            const startDate = getFieldValue(["gainerRecords", field.name, "startDate"]) as string;
                            const endDate = getFieldValue(["gainerRecords", field.name, "endDate"]) as string;
                            if (startDate) {
                              rules.push(validations.notBefore(startDate));
                            }

                            return (
                              <Form.Item
                                name={[field.name, "endDate"]}
                                label={t("contract.attrs.gainerRecords.endDate")}
                                rules={rules}
                                {...datePickerFormItemProps}
                              >
                                <DatePicker
                                  {...datePickerClearableProps}
                                  showNow={false}
                                  disabled={disablePrivilegedInput}
                                  minDate={toDate(startDate)}
                                  defaultPickerValue={toDate(endDate || startDate)}
                                  onChange={() => handleGainerRecordEndDateChange(field.name)}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      )}
                    </Col>
                    {!disablePrivilegedInput && (
                      <Col offset={colSpan * 3} span={colSpan} className="right-align">
                        <ActionButton icon="minus" onClick={() => handleGainerRecordDelete(field.name, remove)}>
                          {t("common.remove")}
                        </ActionButton>
                      </Col>
                    )}
                  </Row>
                )}

                <Row gutter={rowGutter}>
                  {Array.from({ length: gainerCounts[field.name] as number }, (_, i) => i + 1).map((i) => (
                    <React.Fragment key={i}>
                      <Col span={colSpan}>
                        <Form.Item
                          name={[field.name, `gainer${i}Id`]}
                          label={tInterval(`contract.attrs.gainerRecords.gainerId_interval`, i)}
                          rules={[
                            validations.notNull,
                            validations.noRepeatedGainer(gainerCounts[field.name] as number, [
                              "gainerRecords",
                              field.name,
                            ]),
                          ]}
                          initialValue={
                            field.name === 0 && i === 1 && userAccount?.agent?.canBeGainer
                              ? userAccount.agent.id
                              : undefined
                          }
                          dependencies={Array.from({ length: gainerCounts[field.name] as number }, (_, i) => i + 1)
                            .filter((value) => i !== value)
                            .map((value) => ["gainerRecords", field.name, `gainer${value}Id`])}
                        >
                          <AgentSelect
                            optionsProps={{
                              defaultOption:
                                initialContract?.gainerRecords?.[field.name]?.[`gainer${i}` as ContractGainerKey],
                              onlyCanBeGainer: true,
                            }}
                            disabled={disablePrivilegedInput}
                          />
                        </Form.Item>
                      </Col>
                      {(gainerCounts[field.name] as number) > 1 && (
                        <>
                          <Col span={colSpan}>
                            <Form.Item
                              name={[field.name, `gainer${i}Rate`]}
                              dependencies={Array.from({ length: gainerCounts[field.name] as number }, (_, i) => i + 1)
                                .filter((value) => i !== value)
                                .map((value) => ["gainerRecords", field.name, `gainer${value}Rate`])}
                              label={t(`contract.attrs.gainerRecords.gainerRate`, { count: i })}
                              rules={[
                                validations.notNull,
                                validations.minNumber(0),
                                validations.maxNumber(100),
                                validations.gainersRateSumOneHundred(gainerCounts[field.name] as number, [
                                  "gainerRecords",
                                  field.name,
                                ]),
                              ]}
                            >
                              <InputNumber type="decimal" addon="percentage" disabled={disablePrivilegedInput} />
                            </Form.Item>
                          </Col>
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </Row>

                {!disablePrivilegedInput && (
                  <Row gutter={rowGutter} className="margin-top-tiny margin-bottom-small">
                    <Col span={8}>
                      <Space>
                        <ActionButton
                          icon="plus"
                          disabled={(gainerCounts[field.name] as number) >= 5}
                          onClick={() => handleGainerAdd(field.name)}
                        >
                          {t("contract.actions.addGainer")}
                        </ActionButton>

                        <ActionButton
                          icon="minus"
                          disabled={(gainerCounts[field.name] as number) <= 1}
                          onClick={() => handleGainerRemove(field.name)}
                        >
                          {t("contract.actions.removeGainer")}
                        </ActionButton>
                      </Space>
                    </Col>
                  </Row>
                )}
              </div>
            )}
          />

          {!disablePrivilegedInput && (
            <ActionButton
              icon="plus"
              className="margin-top-medium"
              onClick={() => handleGainerRecordAdd(() => add({}))}
            >
              {t("common.add")}
            </ActionButton>
          )}
        </>
      )}
    </Form.List>
  );
};
